import type { ReactNode } from 'react';
import { GridColDef, GridRenderCellParams, type GridComparatorFn } from '@mui/x-data-grid';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import {
  AccessTimeFilled as AccessTimeFilledIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import { StatusIndicator } from '@/components/shared/StatusIndicator';
import type { ThrExtensionInfo } from '@/services/iem';

const resolveDateDisplay = (value?: string | null): string => {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : '--';
};

const formatFriendlyDate = (value?: string | null): string => {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === '--') {
    return '--';
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(parsed);
  }

  return trimmed;
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (value: unknown): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '--';
  }
  return currencyFormatter.format(value);
};

type ExtensionAwareRow = {
  hasExtension?: boolean;
  extension?: ThrExtensionInfo | null;
};

const hasExtensionData = (row: ExtensionAwareRow): boolean => {
  if (row.hasExtension === true) {
    return true;
  }
  const extension = row.extension;
  if (!extension) {
    return false;
  }

  return Boolean(
    extension.reason?.trim() ||
    extension.approvalReference?.trim() ||
    extension.dueDate?.trim() ||
    extension.approvedAt?.trim() ||
    extension.approvedBy?.trim(),
  );
};

const buildExtensionTooltip = (
  extension: ThrExtensionInfo | null | undefined,
  fallbackDueDate: string,
): { content: ReactNode; ariaLabel: string } => {
  const resolvedDueDate = resolveDateDisplay(fallbackDueDate);
  const isConversion = resolvedDueDate === '--';
  const reason = extension?.reason?.trim() ?? 'Not provided.';
  const approvalReference = extension?.approvalReference?.trim() ?? '';
  const approvedByCandidate = extension?.approvedByName ?? extension?.approvedBy;
  const approvedBy = approvedByCandidate?.trim() ?? '';
  const approvedOnRaw = extension?.approvedAt?.trim() ?? '';
  const approvedOn = approvedOnRaw ? formatFriendlyDate(approvedOnRaw) : '';

  const title = isConversion ? 'THR → PHR conversion approved' : 'THR extension approved';
  const reasonLabel = isConversion ? 'Conversion reason' : 'Reason';
  const reasonSentence = /[.!?]$/.test(reason)
    ? `${reasonLabel}: ${reason}`
    : `${reasonLabel}: ${reason}.`;

  const ariaParts = [
    isConversion
      ? 'THR to PHR conversion approved.'
      : `THR extension approved. New due date ${resolvedDueDate}.`,
    reasonSentence,
  ];

  if (isConversion) {
    const approvalReferenceLabel = 'Approval reference';
    const approvalValue = approvalReference ? approvalReference : 'Not provided.';
    const approvalSentence = /[.!?]$/.test(approvalValue)
      ? `${approvalReferenceLabel}: ${approvalValue}`
      : `${approvalReferenceLabel}: ${approvalValue}.`;
    ariaParts.push(approvalSentence);
  }

  if (approvedBy) {
    ariaParts.push(`Processed by ${approvedBy}.`);
  }
  if (approvedOn && approvedOn !== '--') {
    ariaParts.push(`Approved on ${approvedOn}.`);
  }

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography variant="body2" fontWeight={600}>
        {title}
      </Typography>
      {!isConversion && <Typography variant="body2">New due date: {resolvedDueDate}</Typography>}
      <Typography variant="body2">
        {reasonLabel}: {reason}
      </Typography>
      {isConversion && (
        <Typography variant="body2">
          Approval reference: {approvalReference ? approvalReference : 'Not provided.'}
        </Typography>
      )}
      {approvedBy && <Typography variant="body2">Processed by: {approvedBy}</Typography>}
      {approvedOn && approvedOn !== '--' && (
        <Typography variant="body2">Approved on: {approvedOn}</Typography>
      )}
    </Box>
  );

  return {
    content,
    ariaLabel: ariaParts.join(' '),
  };
};

const normalizeStatus = (value?: string | null) => (value ?? '').toLowerCase();

const StatusSeverity = {
  UNAUTHORIZED: 3,
  OVERDUE: 2,
  OVER: 2,
  UNDER: 1,
  COMPLIANT: 0,
  UNKNOWN: -1,
} as const;

const SEVERITY_KEYWORDS: Array<[string, number]> = [
  ['unauthorized', StatusSeverity.UNAUTHORIZED],
  ['overdue', StatusSeverity.OVERDUE],
  ['over limit', StatusSeverity.OVER],
  ['overage', StatusSeverity.OVER],
  ['over', StatusSeverity.OVER],
  ['shortage', StatusSeverity.UNDER],
  ['under', StatusSeverity.UNDER],
  ['compliant', StatusSeverity.COMPLIANT],
];

const statusSeverityWeight = (value?: string | null): number => {
  const normalized = normalizeStatus(value);
  for (const [keyword, weight] of SEVERITY_KEYWORDS) {
    if (normalized.includes(keyword)) return weight;
  }
  return StatusSeverity.UNKNOWN;
};

const statusComparator: GridComparatorFn<string | null | undefined> = (v1, v2) => {
  const w1 = statusSeverityWeight(v1);
  const w2 = statusSeverityWeight(v2);
  if (w1 !== StatusSeverity.UNKNOWN && w2 !== StatusSeverity.UNKNOWN) {
    return w1 - w2;
  }
  return normalizeStatus(v1).localeCompare(normalizeStatus(v2), undefined, { sensitivity: 'base' });
};

const recoverableWeight = (value: boolean | null | undefined) => {
  if (value === true) return 2;
  if (value === false) return 1;
  return 0;
};

const recoverableComparator: GridComparatorFn<boolean | null | undefined> = (v1, v2) =>
  recoverableWeight(v1) - recoverableWeight(v2);

type AllocationRowForUpdate = {
  id: string;
  handReceipt?: 'PHR' | 'THR' | 'PnP';
  status?: string;
  assetId?: string | null;
  personId?: string | null;
  catalogId?: string | null;
  parentCatalogId?: string | null;
  dueDate?: string | null;
  expirationDate?: string | null;
  extension?: ThrExtensionInfo | null;
  hasExtension?: boolean;
  itemIds?: string[];
};

type AllocationStatusKind = 'compliant' | 'shortage' | 'overage' | 'unauthorized' | 'unknown';

type AllocationStatusRow = {
  status?: string;
  statusKind?: AllocationStatusKind;
  unauthorized?: boolean;
  isOverdue?: boolean;
  authorizedQty?: number | null;
  issuedQty?: number | null;
};

type AllocationColumnsOptions = {
  onUpdate?: (handReceiptId: string) => void;
  isRowUpdating?: (handReceiptId: string) => boolean;
  hideUpdate?: boolean;
};

const resolveAllocationRowEligibility = (
  row: AllocationRowForUpdate,
): { eligible: boolean; reason?: string } => {
  if (row.handReceipt !== 'THR' && row.handReceipt !== 'PHR') {
    return { eligible: false, reason: 'Unsupported hand receipt type.' };
  }

  if (!row.assetId && !row.id.startsWith('group:')) {
    return { eligible: false, reason: 'Asset unavailable for update.' };
  }

  if (!row.personId) {
    return { eligible: false, reason: 'Hand receipt is not assigned.' };
  }

  return { eligible: true };
};

export const createIeColumns = (ieKitLabel = 'IE Kit'): GridColDef[] => [
  {
    field: 'kitName',
    headerName: ieKitLabel,
    flex: 1,
    renderHeader: () => (
      <Tooltip title={`Assigned ${ieKitLabel}`} arrow enterTouchDelay={0}>
        <span style={{ fontWeight: 500 }}>{ieKitLabel}</span>
      </Tooltip>
    ),
    renderCell: (params: GridRenderCellParams) => {
      const label = typeof params.value === 'string' ? params.value : String(params.value ?? '');
      return (
        <Tooltip title={label} arrow enterTouchDelay={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <Typography variant="body2" noWrap sx={{ width: '100%' }}>
              {label}
            </Typography>
          </Box>
        </Tooltip>
      );
    },
  },
  {
    field: 'lin',
    headerName: 'LIN',
    width: 140,
    valueFormatter: (value) => (value ? String(value) : '--'),
  },
  { field: 'nsn', headerName: 'NSN', flex: 1 },
  { field: 'item', headerName: 'Nomenclature', flex: 1.2 },
  {
    field: 'unitPrice',
    headerName: 'Unit Price',
    width: 130,
    type: 'number',
    align: 'right',
    headerAlign: 'right',
    valueFormatter: (value) => formatCurrency(value),
  },
  { field: 'authorizedQty', headerName: 'Qty Authorized', width: 140 },
  { field: 'currentQty', headerName: 'Qty Issued', width: 120 },
  {
    field: 'status',
    headerName: 'Status',
    width: 180,
    renderCell: (params: GridRenderCellParams) => {
      const row = params.row as { authorizedQty?: number; currentQty?: number };
      const a = row.authorizedQty ?? 0;
      const i = row.currentQty ?? 0;
      let reasonLabel: string | null = null;
      if (a === 0 && i > 0) {
        reasonLabel = 'Not Authorized';
      } else if (i > a && a > 0) {
        reasonLabel = 'Qty Over';
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <StatusIndicator
            authorizedQty={row.authorizedQty}
            issuedQty={row.currentQty}
            size="small"
          />
          {reasonLabel && (
            <Typography variant="caption" noWrap>
              {reasonLabel}
            </Typography>
          )}
        </Box>
      );
    },
    sortable: true,
    sortComparator: statusComparator,
  },
];

export const createAllocationColumns = (
  options: AllocationColumnsOptions = {},
  ieKitLabel = 'IE Kit',
): GridColDef[] => [
  {
    field: 'kitName',
    headerName: ieKitLabel,
    width: 140,
    renderHeader: () => (
      <Tooltip title={`Assigned ${ieKitLabel}`} arrow enterTouchDelay={0}>
        <span style={{ fontWeight: 500 }}>{ieKitLabel}</span>
      </Tooltip>
    ),
    valueFormatter: (value) => (value ? String(value) : '--'),
  },
  {
    field: 'lin',
    headerName: 'LIN',
    width: 90,
    valueFormatter: (value) => (value ? String(value) : '--'),
  },
  { field: 'nsn', headerName: 'NSN', width: 110 },
  {
    field: 'item',
    headerName: 'Nomenclature',
    flex: 1.6,
    minWidth: 240,
    valueFormatter: (value) => (value ? String(value) : '--'),
    renderCell: (params: GridRenderCellParams) => {
      const label = typeof params.value === 'string' ? params.value : String(params.value ?? '--');
      return (
        <Tooltip title={label} arrow enterTouchDelay={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <Typography variant="body2" noWrap sx={{ width: '100%' }}>
              {label}
            </Typography>
          </Box>
        </Tooltip>
      );
    },
  },
  { field: 'quantity', headerName: 'Qty', width: 70 },
  {
    field: 'cost',
    headerName: 'Cost',
    width: 110,
    type: 'number',
    align: 'right',
    headerAlign: 'left',
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: 'handReceipt',
    headerName: 'Hand Receipt (HR)',
    width: 80,
    renderHeader: () => <span style={{ fontWeight: 500 }}>HR</span>,
    renderCell: (params: GridRenderCellParams) => {
      const value = params.value as 'PHR' | 'THR' | 'PnP' | undefined;
      const label = value ?? '--';
      const showExtension = hasExtensionData(params.row);
      const extension = params.row.extension ?? null;
      const dueDate = params.row.dueDate ?? '--';
      const tooltip = showExtension ? buildExtensionTooltip(extension, dueDate) : null;

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">{label}</Typography>
          {showExtension && tooltip ? (
            <Tooltip title={tooltip.content} arrow enterTouchDelay={0}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
                <EventAvailableIcon
                  fontSize="small"
                  color="primary"
                  aria-label={tooltip.ariaLabel}
                />
              </Box>
            </Tooltip>
          ) : null}
        </Box>
      );
    },
  },
  {
    field: 'issueDate',
    headerName: 'Issue Date',
    width: 120,
    renderCell: (params: GridRenderCellParams) => {
      const raw =
        typeof params.value === 'string'
          ? params.value
          : (params.row as Record<string, unknown>)?.issueDate;
      const display = resolveDateDisplay(typeof raw === 'string' ? raw : null);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2" color={display === '--' ? 'text.secondary' : 'inherit'}>
            {display}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: 'dueDate',
    headerName: 'Due / Return',
    width: 120,
    renderCell: (params: GridRenderCellParams) => {
      const value = typeof params.value === 'string' ? params.value : '--';
      const row = params.row as AllocationStatusRow;
      const statusVal = String(row.status ?? '');
      const overdue = row.isOverdue === true || statusVal.toLowerCase().includes('overdue');
      const displayValue = resolveDateDisplay(value);

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography
            variant="body2"
            color={overdue ? 'error' : 'inherit'}
            fontWeight={overdue ? 600 : undefined}
          >
            {displayValue}
          </Typography>
        </Box>
      );
    },
    sortable: true,
  },
  {
    field: 'expirationDate',
    headerName: 'Expiration Date',
    width: 140,
    renderCell: (params: GridRenderCellParams) => {
      const value = typeof params.value === 'string' ? params.value : null;
      const display = resolveDateDisplay(value);
      if (display === '--') {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              {display}
            </Typography>
          </Box>
        );
      }
      const parsed = new Date(display);
      const now = new Date();
      const daysUntil = Number.isNaN(parsed.getTime())
        ? null
        : Math.floor((parsed.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      const expired = daysUntil !== null && daysUntil < 0;
      const approaching = daysUntil !== null && daysUntil >= 0 && daysUntil <= 60;
      const color = expired ? 'error' : approaching ? 'warning' : 'success';
      const friendly = formatFriendlyDate(value);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip label={friendly} size="small" color={color} />
        </Box>
      );
    },
    sortable: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: (params: GridRenderCellParams) => {
      const row = params.row as AllocationStatusRow;
      const label = typeof params.value === 'string' ? params.value : 'Unknown';
      const explicitKind: AllocationStatusKind =
        row.statusKind ??
        (row.unauthorized || label.toLowerCase().includes('unauthorized')
          ? 'unauthorized'
          : 'unknown');

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%' }}>
          <StatusIndicator
            explicitKind={explicitKind}
            authorizedQty={row.authorizedQty}
            issuedQty={row.issuedQty}
            size="small"
          />
          {row.isOverdue ? (
            <Tooltip title="Overdue — THR item is past its return due date.">
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <AccessTimeFilledIcon
                  fontSize="small"
                  color="error"
                  aria-label="Overdue"
                  titleAccess="Overdue"
                />
              </Box>
            </Tooltip>
          ) : null}
        </Box>
      );
    },
    sortable: true,
    sortComparator: statusComparator,
  },
  {
    field: 'catalogType',
    headerName: 'Type',
    width: 130,
    renderCell: (params: GridRenderCellParams) => {
      const label = String(params.value ?? '');
      return <Chip label={label} size="small" variant="outlined" />;
    },
  },
  {
    field: 'recoverable',
    headerName: 'Recoverable',
    width: 160,
    renderCell: (params: GridRenderCellParams) => {
      const row = params.row as { unauthorized?: boolean };
      const isUnauthorized = Boolean(row?.unauthorized);

      if (params.value === false) {
        // If row is unauthorized (amber background), use 'default' (grey) for better contrast
        // instead of 'warning' (orange).
        return (
          <Chip
            label="Non-Recoverable"
            size="small"
            color={isUnauthorized ? 'default' : 'warning'}
          />
        );
      }
      if (params.value === true) {
        return <Chip label="Recoverable" size="small" color="success" variant="outlined" />;
      }
      return <Chip label="--" size="small" variant="outlined" />;
    },
    sortable: true,
    sortComparator: recoverableComparator,
  },
  ...(options.hideUpdate
    ? []
    : [
        {
          field: 'actions' as const,
          headerName: '',
          width: 320,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          align: 'center' as const,
          renderCell: (params: GridRenderCellParams) => {
            const row = params.row as AllocationRowForUpdate;
            const { eligible, reason } = resolveAllocationRowEligibility(row);
            const isUpdating = options.isRowUpdating?.(row.id) ?? false;
            const disabled = !eligible || isUpdating;

            const button = (
              <Button
                size="small"
                variant="outlined"
                disabled={disabled}
                onClick={() => {
                  if (!disabled) {
                    options.onUpdate?.(row.id);
                  }
                }}
              >
                {isUpdating ? 'Updating…' : 'Update'}
              </Button>
            );

            if (disabled && reason) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <span>{button}</span>
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ lineHeight: 1.2, whiteSpace: 'normal' }}
                  >
                    {reason}
                  </Typography>
                </Box>
              );
            }

            return button;
          },
        },
      ]),
];
